import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, CheckCircle2, Send, Loader2, Clock, ShieldCheck, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { LineReveal, Reveal } from "@/components/Reveal";
import { BUSINESS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = ["House", "Apartment", "Office", "Commercial", "Other"];
const CLEANING_TYPES = [
  "Residential Cleaning", "Deep Cleaning", "Recurring Cleaning", "Move-In Cleaning",
  "Move-Out Cleaning", "Post-Construction Cleanup", "Apartment Cleaning", "Office Cleaning", "Commercial Cleaning", "Other",
];

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-slate-50/50 px-4 py-3.5 text-base text-slate-900 placeholder:text-slate-400 transition-all focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-semibold text-slate-800">{label}</label>
    {children}
  </div>
);

export default function QuotePage() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", property_type: "", cleaning_type: "", preferred_date: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, {
        ...form,
        preferred_date: form.preferred_date || null,
        message: form.message || null,
      });
      setDone(true);
      toast.success("Quote request sent! We'll be in touch soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Get a Free Cleaning Quote | Julisa's House Cleaning — Kansas City"
        description="Request a free, no-pressure cleaning quote for your home, apartment, office or commercial space in Overland Park, Lenexa, Olathe, Kansas City KS or Kansas City MO."
      />

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-200/60 via-sky-100/50 to-sky-50 pt-14 pb-16 sm:pt-20 sm:pb-24">
        <div className="pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-sky-100/70 blur-3xl" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <p className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
                Free Quote
              </p>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
                <LineReveal lines={["Tell us about", <>your <span className="text-sky-600 italic">space</span></>]} />
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
                Fill out the form and we'll get back to you with a free, no-pressure quote.
                Prefer to talk? We're a phone call away.
              </p>

              <div className="mt-8 space-y-4">
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="quote-page-phone-link"
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:border-sky-300 hover:shadow-lg"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-600 text-white">
                    <Phone className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Call</span>
                    <span className="block text-lg font-bold text-slate-900">{BUSINESS.phone}</span>
                  </span>
                </a>
                <a
                  href={BUSINESS.emailHref}
                  data-testid="quote-page-email-link"
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:border-sky-300 hover:shadow-lg"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-sky-600 text-white">
                    <Mail className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</span>
                    <span className="block truncate text-sm sm:text-base font-bold text-slate-900">{BUSINESS.email}</span>
                  </span>
                </a>
              </div>

              <ul className="mt-8 space-y-3 text-sm text-slate-600">
                {[
                  { icon: Clock, text: "We typically respond within one business day." },
                  { icon: ShieldCheck, text: "No pressure, no obligation — just an honest quote." },
                  { icon: Sparkles, text: "Serving the entire Kansas City metro area." },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-3">
                    <item.icon className="h-4 w-4 shrink-0 text-sky-600" aria-hidden="true" />
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>

            <Reveal delay={0.15} className="lg:col-span-3">
              <div className="rounded-3xl border border-sky-200 bg-white p-6 sm:p-10 shadow-xl shadow-sky-900/10">
                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                      className="flex flex-col items-center py-14 text-center"
                      data-testid="quote-success-message"
                    >
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                        <CheckCircle2 className="h-9 w-9" aria-hidden="true" />
                      </span>
                      <h2 className="mt-6 font-display text-2xl sm:text-3xl font-semibold text-slate-900">
                        Thank you, {form.name.split(" ")[0]}!
                      </h2>
                      <p className="mt-3 max-w-md text-base text-slate-600">
                        Your quote request has been sent. We'll review the details and get back to
                        you shortly — usually within one business day.
                      </p>
                      <p className="mt-4 text-sm text-slate-500">
                        Need a faster answer? Call us at{" "}
                        <a href={BUSINESS.phoneHref} data-testid="quote-success-call-link" className="font-semibold text-sky-600 hover:underline">
                          {BUSINESS.phone}
                        </a>
                      </p>
                      <Link
                        to="/"
                        data-testid="quote-success-home-link"
                        className="mt-8 rounded-xl border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        Back to Home
                      </Link>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      onSubmit={handleSubmit}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, y: -12 }}
                      className="grid gap-5 sm:grid-cols-2"
                      data-testid="quote-form"
                    >
                      <Field label="Name" htmlFor="q-name">
                        <input id="q-name" data-testid="quote-form-name-input" required type="text" autoComplete="name" placeholder="Your full name" className={inputCls} value={form.name} onChange={set("name")} />
                      </Field>
                      <Field label="Phone Number" htmlFor="q-phone">
                        <input id="q-phone" data-testid="quote-form-phone-input" required type="tel" autoComplete="tel" placeholder="(913) 555-0123" className={inputCls} value={form.phone} onChange={set("phone")} />
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Email" htmlFor="q-email">
                          <input id="q-email" data-testid="quote-form-email-input" required type="email" autoComplete="email" placeholder="you@example.com" className={inputCls} value={form.email} onChange={set("email")} />
                        </Field>
                      </div>
                      <Field label="Property Type" htmlFor="q-property">
                        <select id="q-property" data-testid="quote-form-property-type-select" required className={inputCls} value={form.property_type} onChange={set("property_type")}>
                          <option value="" disabled>Select property type</option>
                          {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <Field label="Type of Cleaning" htmlFor="q-cleaning">
                        <select id="q-cleaning" data-testid="quote-form-cleaning-type-select" required className={inputCls} value={form.cleaning_type} onChange={set("cleaning_type")}>
                          <option value="" disabled>Select cleaning type</option>
                          {CLEANING_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Preferred Date (optional)" htmlFor="q-date">
                          <input id="q-date" data-testid="quote-form-date-input" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Message / Additional Details (optional)" htmlFor="q-message">
                          <textarea id="q-message" data-testid="quote-form-message-input" rows={4} placeholder="Tell us about your space — size, rooms, pets, anything that helps us quote accurately." className={`${inputCls} resize-y`} value={form.message} onChange={set("message")} />
                        </Field>
                      </div>
                      <div className="sm:col-span-2">
                        <button
                          type="submit"
                          data-testid="quote-form-submit-button"
                          disabled={submitting}
                          className="flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-7 py-4 text-base font-bold text-white shadow-lg shadow-sky-600/25 transition-all duration-200 hover:bg-sky-700 hover:shadow-xl active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {submitting ? (
                            <><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Sending…</>
                          ) : (
                            <><Send className="h-5 w-5" aria-hidden="true" /> Request My Free Quote</>
                          )}
                        </button>
                        <p className="mt-3 text-center text-xs text-slate-500">
                          Your details go straight to Julisa's House Cleaning — never shared or sold.
                        </p>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
